import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import {
  User, MapPin, Shield, Save, CheckCircle, AlertTriangle,
  Crown, Calendar, Hash, Phone, Mail, Globe, ShieldCheck, RotateCcw, Loader2, Send
} from 'lucide-react';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfileField = ({ label, name, value, onChange, placeholder, type = 'text', icon: Icon, hint, disabled, maxLength }) => (
  <div className="space-y-1.5">
    <Label htmlFor={name} className="text-sm font-medium flex items-center gap-1.5" style={{ color: 'var(--text-primary)' }}>
      {Icon && <Icon className="h-3.5 w-3.5" style={{ color: 'var(--primary-300)' }} />}
      {label}
    </Label>
    <Input
      id={name}
      name={name}
      type={type}
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={maxLength}
      className="h-10"
      data-testid={`profile-field-${name}`}
    />
    {hint && <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{hint}</p>}
  </div>
);

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [resettingTour, setResettingTour] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const isParent = user?.role === 'parent';
  const isNanny = user?.role === 'nanny';

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/profile`);
      setProfile(res.data);
      setFormData({
        first_name: res.data.first_name || '',
        last_name: res.data.last_name || '',
        maiden_name: res.data.maiden_name || '',
        birth_date: res.data.birth_date || '',
        birth_place: res.data.birth_place || '',
        phone: res.data.phone || '',
        address_street: res.data.address?.street || '',
        address_postal_code: res.data.address?.postal_code || '',
        address_city: res.data.address?.city || '',
        address_country: res.data.address?.country || 'France',
        social_security_number: res.data.social_security_number || '',
        pajemploi_number: res.data.pajemploi_number || '',
        caf_number: res.data.caf_number || '',
        agrement_number: res.data.agrement_number || '',
        agrement_start_date: res.data.agrement_start_date || '',
        agrement_end_date: res.data.agrement_end_date || '',
        insurance_company: res.data.insurance_company || '',
        insurance_policy_number: res.data.insurance_policy_number || '',
        insurance_expiry_date: res.data.insurance_expiry_date || '',
      });
      setHasChanges(false);
    } catch (err) {
      console.error('Profile fetch error:', err);
      toast.error('Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setHasChanges(true);
    // Reset Pajemploi validation when number changes
    if (name === 'pajemploi_number') {
      setPajemploiValidation(null);
    }
  };

  // Pajemploi number validation
  const [pajemploiValidation, setPajemploiValidation] = useState(null);
  const [validatingPajemploi, setValidatingPajemploi] = useState(false);

  const verifyPajemploiNumber = async () => {
    if (!formData.pajemploi_number || formData.pajemploi_number.length < 5) {
      setPajemploiValidation(null);
      return;
    }
    // Save first so the API verifies the latest value
    setValidatingPajemploi(true);
    try {
      // Quick save of the pajemploi_number + birth_date before verifying
      await axios.put(`${API}/profile`, {
        pajemploi_number: formData.pajemploi_number,
        birth_date: formData.birth_date || undefined,
      });
      const res = await axios.post(`${API}/profile/verify-pajemploi-number`);
      setPajemploiValidation(res.data);
    } catch (err) {
      setPajemploiValidation({ valid: false, message: "Erreur de vérification" });
    } finally {
      setValidatingPajemploi(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        maiden_name: formData.maiden_name || null,
        birth_date: formData.birth_date || null,
        birth_place: formData.birth_place || null,
        phone: formData.phone || null,
        address: {
          street: formData.address_street || null,
          postal_code: formData.address_postal_code || null,
          city: formData.address_city || null,
          country: formData.address_country || 'France',
        },
        social_security_number: formData.social_security_number || null,
        pajemploi_number: formData.pajemploi_number || null,
        caf_number: formData.caf_number || null,
        agrement_number: formData.agrement_number || null,
        agrement_start_date: formData.agrement_start_date || null,
        agrement_end_date: formData.agrement_end_date || null,
        insurance_company: formData.insurance_company || null,
        insurance_policy_number: formData.insurance_policy_number || null,
        insurance_expiry_date: formData.insurance_expiry_date || null,
      };

      const res = await axios.put(`${API}/profile`, payload);
      setProfile(res.data);
      setHasChanges(false);
      toast.success('Profil mis à jour avec succès');
      // Refresh user context so Dashboard alert updates
      if (refreshUser) refreshUser();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur lors de la sauvegarde';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleResetTour = async () => {
    try {
      setResettingTour(true);
      await axios.post(`${API}/profile/reset-tour`);
      window.dispatchEvent(new CustomEvent('nannyou-reset-tour'));
      toast.success('Visite guidée réinitialisée — redirection…');
      setTimeout(() => navigate('/accueil'), 600);
    } catch (err) {
      toast.error('Erreur lors de la réinitialisation');
    } finally {
      setResettingTour(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setResendingEmail(true);
      const res = await axios.post(`${API}/auth/resend-verification`);
      if (res.data.already_verified) {
        toast.success('Votre email est déjà vérifié !');
        fetchProfile();
        if (refreshUser) refreshUser();
      } else if (res.data.sent) {
        toast.success('Email de vérification envoyé ! Vérifiez votre boîte de réception.');
      } else {
        toast.info(res.data.message || "Impossible d'envoyer l'email pour le moment.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi de l'email");
    } finally {
      setResendingEmail(false);
    }
  };

  const getCompletenessColor = (pct) => {
    if (pct >= 80) return 'var(--success)';
    if (pct >= 50) return 'var(--warning)';
    return 'var(--error)';
  };

  if (loading) {
    return (
      <div className="container-custom py-6 space-y-4 animate-fade-in">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-6 w-72" />
        <Skeleton className="h-[400px] w-full rounded-xl" />
      </div>
    );
  }

  const completeness = profile?.profile_completeness || 0;

  return (
    <div className="container-custom py-6 space-y-6 animate-fade-in" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--text-primary)' }}>
          Mon profil
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
          Informations nécessaires pour les déclarations Pajemploi
        </p>
      </div>

      {/* Completeness Card */}
      <Card className="border" style={{ borderColor: 'var(--neutral-200)' }}>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {completeness >= 80 ? (
                <CheckCircle className="h-5 w-5" style={{ color: 'var(--success)' }} />
              ) : (
                <AlertTriangle className="h-5 w-5" style={{ color: getCompletenessColor(completeness) }} />
              )}
              <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                Complétude du profil
              </span>
            </div>
            <Badge
              variant="outline"
              style={{
                borderColor: getCompletenessColor(completeness),
                color: getCompletenessColor(completeness),
              }}
              data-testid="profile-completeness-badge"
            >
              {completeness}%
            </Badge>
          </div>
          <Progress
            value={completeness}
            className="h-2"
            data-testid="profile-completeness-bar"
          />
          {completeness < 80 && (
            <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
              Complétez votre profil pour pouvoir effectuer vos déclarations Pajemploi.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Profile Info */}
      <div className="flex items-center gap-3 px-1">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
          style={{ backgroundColor: 'var(--primary-100)', color: 'var(--primary-400)' }}
        >
          <User className="h-6 w-6" />
        </div>
        <div>
          <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{profile?.full_name}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{profile?.email}</span>
            <Badge variant="outline" className={isParent
              ? 'bg-[var(--secondary-50)] text-[var(--secondary-400)] border-[var(--secondary-400)]'
              : 'bg-[var(--primary-100)] text-[var(--primary-400)] border-[var(--primary-400)]'
            } style={{ fontSize: '10px' }}>
              {isParent ? 'Parent' : 'Nounou'}
            </Badge>
            {profile?.is_premium && (
              <Badge className="bg-[var(--secondary-400)] text-white" style={{ fontSize: '10px' }}>
                <Crown className="h-3 w-3 mr-1" />Pro
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Email Verification Status — only show when NOT verified */}
      {profile && !profile.email_verified && (
        <Card
          className="border"
          style={{ borderColor: 'var(--warning)' }}
          data-testid="email-verification-card"
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                  style={{ backgroundColor: 'var(--warning)' }}
                >
                  <Mail className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Email non vérifié
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    Vérifiez votre email pour sécuriser votre compte
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleResendVerification}
                disabled={resendingEmail}
                className="gap-1.5 shrink-0"
                style={{ borderColor: 'var(--warning)', color: 'var(--warning)' }}
                data-testid="resend-verification-btn"
              >
                {resendingEmail
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Send className="h-3.5 w-3.5" />
                }
                {resendingEmail ? 'Envoi…' : 'Renvoyer le lien'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="identity" className="w-full">
        <TabsList className={cn("w-full h-auto p-1", isNanny ? "grid grid-cols-5" : "grid grid-cols-4")} style={{ backgroundColor: 'var(--neutral-100)' }}>
          <TabsTrigger
            value="identity"
            className="text-xs sm:text-sm py-2 data-[state=active]:bg-[var(--bg-primary)] data-[state=active]:text-[var(--primary-400)] data-[state=active]:shadow-sm"
            data-testid="tab-identity"
          >
            <User className="h-4 w-4 mr-1 hidden sm:block" />
            Identité
          </TabsTrigger>
          <TabsTrigger
            value="address"
            className="text-xs sm:text-sm py-2 data-[state=active]:bg-[var(--bg-primary)] data-[state=active]:text-[var(--primary-400)] data-[state=active]:shadow-sm"
            data-testid="tab-address"
          >
            <MapPin className="h-4 w-4 mr-1 hidden sm:block" />
            Adresse
          </TabsTrigger>
          <TabsTrigger
            value="admin"
            className="text-xs sm:text-sm py-2 data-[state=active]:bg-[var(--bg-primary)] data-[state=active]:text-[var(--primary-400)] data-[state=active]:shadow-sm"
            data-testid="tab-admin"
          >
            <Shield className="h-4 w-4 mr-1 hidden sm:block" />
            Admin
          </TabsTrigger>
          {isNanny && (
            <TabsTrigger
              value="insurance"
              className="text-xs sm:text-sm py-2 data-[state=active]:bg-[var(--bg-primary)] data-[state=active]:text-[var(--primary-400)] data-[state=active]:shadow-sm"
              data-testid="tab-insurance"
            >
              <ShieldCheck className="h-4 w-4 mr-1 hidden sm:block" />
              Assurance
            </TabsTrigger>
          )}
        </TabsList>

        {/* ── Tab: Identité ── */}
        <TabsContent value="identity" className="mt-4">
          <Card style={{ borderColor: 'var(--neutral-200)' }}>
            <CardHeader className="pb-4">
              <CardTitle className="text-base" style={{ color: 'var(--text-primary)' }}>Identité</CardTitle>
              <CardDescription>Vos informations personnelles</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ProfileField
                  label="Prénom" name="first_name"
                  value={formData.first_name} onChange={handleChange}
                  placeholder="Jean" icon={User}
                />
                <ProfileField
                  label="Nom" name="last_name"
                  value={formData.last_name} onChange={handleChange}
                  placeholder="Dupont" icon={User}
                />
              </div>
              {isNanny && (
                <ProfileField
                  label="Nom de naissance" name="maiden_name"
                  value={formData.maiden_name} onChange={handleChange}
                  placeholder="Nom de jeune fille" icon={User}
                  hint="Si différent du nom actuel"
                />
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ProfileField
                  label="Date de naissance" name="birth_date"
                  value={formData.birth_date} onChange={handleChange}
                  type="date" icon={Calendar}
                />
                <ProfileField
                  label="Lieu de naissance" name="birth_place"
                  value={formData.birth_place} onChange={handleChange}
                  placeholder="Paris" icon={MapPin}
                />
              </div>
              <div className="grid grid-cols-1 gap-4">
                <ProfileField
                  label="Téléphone" name="phone"
                  value={formData.phone} onChange={handleChange}
                  placeholder="06 12 34 56 78" type="tel" icon={Phone}
                />
              </div>
              <div className="pt-2">
                <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                  <Mail className="h-3.5 w-3.5" />
                  <span>Email : <strong>{profile?.email}</strong> (non modifiable)</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: Adresse ── */}
        <TabsContent value="address" className="mt-4">
          <Card style={{ borderColor: 'var(--neutral-200)' }}>
            <CardHeader className="pb-4">
              <CardTitle className="text-base" style={{ color: 'var(--text-primary)' }}>Coordonnées</CardTitle>
              <CardDescription>Recherchez votre adresse — les champs se remplissent automatiquement</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <AddressAutocomplete
                value={{
                  street: formData.address_street,
                  postal_code: formData.address_postal_code,
                  city: formData.address_city,
                  country: formData.address_country,
                }}
                onChange={(addr) => {
                  setFormData(prev => ({
                    ...prev,
                    address_street: addr.street || '',
                    address_postal_code: addr.postal_code || '',
                    address_city: addr.city || '',
                    address_country: addr.country || 'France',
                  }));
                  setHasChanges(true);
                }}
              />
              {/* Show filled fields after autocomplete (editable) */}
              <div className="grid grid-cols-2 gap-4">
                <ProfileField
                  label="Rue" name="address_street"
                  value={formData.address_street} onChange={handleChange}
                  placeholder="12 Rue de la Paix" icon={MapPin}
                />
                <ProfileField
                  label="Code postal" name="address_postal_code"
                  value={formData.address_postal_code} onChange={handleChange}
                  placeholder="75001" maxLength={5}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <ProfileField
                  label="Ville" name="address_city"
                  value={formData.address_city} onChange={handleChange}
                  placeholder="Paris"
                />
                <ProfileField
                  label="Pays" name="address_country"
                  value={formData.address_country} onChange={handleChange}
                  placeholder="France" icon={Globe}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: Admin ── */}
        <TabsContent value="admin" className="mt-4">
          <Card style={{ borderColor: 'var(--neutral-200)' }}>
            <CardHeader className="pb-4">
              <CardTitle className="text-base" style={{ color: 'var(--text-primary)' }}>Informations administratives</CardTitle>
              <CardDescription>
                {isParent ? 'Identifiants employeur pour Pajemploi' : 'Identifiants salariée pour Pajemploi'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!isParent && (
                <ProfileField
                  label="N° Sécurité sociale" name="social_security_number"
                  value={formData.social_security_number} onChange={handleChange}
                  placeholder="1 85 12 75 108 234 56" icon={Shield}
                  hint="15 chiffres — figurant sur votre carte Vitale"
                  maxLength={15}
                />
              )}

              {isParent && (
                <>
                  <Separator />
                  <div className="space-y-1.5">
                    <Label htmlFor="pajemploi_number" className="text-sm font-medium flex items-center gap-1.5" style={{ color: 'var(--text-primary)' }}>
                      <Hash className="h-3.5 w-3.5" style={{ color: 'var(--primary-300)' }} />
                      N° Pajemploi
                    </Label>
                    <div className="relative">
                      <Input
                        id="pajemploi_number"
                        name="pajemploi_number"
                        value={formData.pajemploi_number || ''}
                        onChange={handleChange}
                        onBlur={verifyPajemploiNumber}
                        placeholder="Y12345678901"
                        className="h-10"
                        data-testid="profile-field-pajemploi_number"
                      />
                      {validatingPajemploi && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="h-4 w-4 border-2 border-[var(--primary-400)] border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Commence par Y — reçu lors de l'inscription Pajemploi</p>
                    {pajemploiValidation && !validatingPajemploi && (
                      <div
                        className={cn(
                          "flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md",
                          pajemploiValidation.valid
                            ? "bg-[var(--success-light)] text-[var(--success)]"
                            : "bg-[var(--error)]/5 text-[var(--error)]"
                        )}
                        data-testid="pajemploi-validation-result"
                      >
                        {pajemploiValidation.valid
                          ? <CheckCircle className="h-3.5 w-3.5 shrink-0" />
                          : <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        }
                        {pajemploiValidation.message}
                      </div>
                    )}
                  </div>
                </>
              )}

              {isNanny && (
                <>
                  <Separator />
                  <p className="text-xs italic" style={{ color: 'var(--text-muted)' }}>Ces champs sont optionnels et n'affectent pas la complétude du profil.</p>
                  <ProfileField
                    label="N° d'agrément (optionnel)" name="agrement_number"
                    value={formData.agrement_number} onChange={handleChange}
                    placeholder="PMI-2024-XXXXX" icon={Hash}
                    hint="Délivré par le Conseil départemental"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <ProfileField
                      label="Délivrance (optionnel)" name="agrement_start_date"
                      value={formData.agrement_start_date} onChange={handleChange}
                      type="date" icon={Calendar}
                    />
                    <ProfileField
                      label="Fin de validité (optionnel)" name="agrement_end_date"
                      value={formData.agrement_end_date} onChange={handleChange}
                      type="date" icon={Calendar}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: Assurance (Nanny only) ── */}
        {isNanny && (
          <TabsContent value="insurance" className="mt-4">
            <Card style={{ borderColor: 'var(--neutral-200)' }}>
              <CardHeader className="pb-4">
                <CardTitle className="text-base" style={{ color: 'var(--text-primary)' }}>Assurance RC Professionnelle</CardTitle>
                <CardDescription>Responsabilité civile professionnelle obligatoire — <span className="italic">ces champs sont optionnels pour la complétude du profil</span></CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ProfileField
                  label="Nom de l'assureur (optionnel)" name="insurance_company"
                  value={formData.insurance_company} onChange={handleChange}
                  placeholder="MAIF, Groupama, AXA..." icon={ShieldCheck}
                />
                <ProfileField
                  label="N° de police (optionnel)" name="insurance_policy_number"
                  value={formData.insurance_policy_number} onChange={handleChange}
                  placeholder="POL-2024-XXXXX" icon={Hash}
                />
                <ProfileField
                  label="Date d'expiration (optionnel)" name="insurance_expiry_date"
                  value={formData.insurance_expiry_date} onChange={handleChange}
                  type="date" icon={Calendar}
                  hint="Date de fin de validité de votre contrat RC Pro"
                />
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* ── Relancer la visite guidée ── */}
      <Separator />
      <div className="flex items-center justify-between px-1" data-testid="restart-tour-section">
        <div>
          <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Visite guidée</p>
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Relancez la visite pour redécouvrir les fonctionnalités</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleResetTour}
          disabled={resettingTour}
          className="gap-1.5 shrink-0"
          style={{ borderColor: 'var(--primary-300)', color: 'var(--primary-400)' }}
          data-testid="restart-tour-btn"
        >
          <RotateCcw className={cn("h-3.5 w-3.5", resettingTour && "animate-spin")} />
          {resettingTour ? 'Réinitialisation…' : 'Relancer la visite'}
        </Button>
      </div>

      {/* Save Button - Sticky on mobile */}
      <div
        className="sticky bottom-20 md:bottom-4 z-40 flex justify-end"
        data-testid="profile-save-area"
      >
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className={cn(
            "shadow-lg transition-all duration-200 gap-2",
            hasChanges
              ? "bg-[var(--primary-400)] hover:bg-[var(--primary-500)] text-white"
              : "bg-[var(--neutral-200)] text-[var(--text-secondary)] cursor-not-allowed"
          )}
          size="lg"
          data-testid="profile-save-button"
        >
          <Save className="h-4 w-4" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>
    </div>
  );
}
